/**
 * THE AD CLAIM VOCABULARY: the one list of claim patterns the paid-ad rules run, owned by the
 * compliance desk seat (`docs/BOARD-OF-ADVISORS.md`). A pattern is not loosened or widened here
 * without that seat.
 *
 * Two callers run it: `checkAdContract` (the finished ad, `ad-contract.ts`) and the seller-fact
 * check (`src/lib/paid-media/seller-facts.ts`, a line a person types about their own product), so
 * a stored fact and the ad that quotes it answer to the same list.
 *
 * NO IMPORTS, on purpose. `paid-copy-slop-gate.ts` ships in an edge bundle without an import map,
 * and `ad-contract.ts` imports path-aliased modules, so nothing that bundle needs may live there.
 * This file imports nothing, so a Deno bundle can load it by a relative path. The one literal the
 * bundle already re-declares (`SLOP_VOCABULARY_PATTERN` in `paid-copy-slop-gate.ts`) stays a
 * byte-for-byte copy of the one below, held by `paid-copy-slop-gate.test.ts` and the Deno test
 * beside the edge function.
 *
 * CLASSES. The nine the compliance desk named (health, certification, origin, award, comparative,
 * certainty, performance, scarcity, social proof) plus the slop vocabulary the contract already
 * ran. `AD_CONTRACT_CLAIM_CLASSES` is what `checkAdContract` runs, unchanged from before this file
 * existed; the other four classes (health, certification, origin, award, comparative) are run on a
 * seller fact today and are not yet run on the finished ad, so the contract's behaviour did not
 * change when the vocabulary moved here. Widening the contract to them is a compliance desk call.
 *
 * KNOWN LIMITATION, not loosened: the certainty and performance patterns are word lists, so they
 * refuse some TRUE seller facts, for example "Always machine washable.", "Fits exactly 54 to 62
 * cm.", "The perfect size for a carry-on." and "Adds stability in wind.". The seller-fact check
 * says "Mora will not use this line. The ad rules do not allow this claim." for each. The origin
 * class covers United States origin claims only (the regulated one); other countries pass.
 */

export const AD_CLAIM_VOCABULARY_VERSION = 'ad-claim-vocabulary-2026-10-22.1' as const;

/**
 * A claim of social proof — NOT the bare nouns social proof happens to use.
 *
 * 🔴 WHY THIS IS A CONSTRUCTION AND NOT A WORD LIST. It used to be
 * `/\b(fans?|customers?|reviews?|testimonials?|ratings?|trusted by|loved by|
 * already saying)\b/i`, matching the bare nouns `fans` and `customers` with no
 * claim attached. Measured against real copy for a tennis product:
 *
 *   BLOCKED  "Built for fans of the baseline game."          ← ordinary sport usage
 *   BLOCKED  "New customers get free shipping on their first order."  ← an offer term
 *
 * Neither asserts that anyone endorsed anything, and every GENUINE fabrication
 * in the same sample was caught by its construction instead — "trusted by",
 * "loved by", "rated 4.8 stars by 2,000 customers", "our customers say". So the
 * bare nouns were contributing false positives and nothing else.
 *
 * 🔴 SECOND ROUND: singular `review` and bare `rated` were STILL a word list.
 * `\brated\b` and the singular half of `\breviews?\b` fired on ordinary verb
 * and adjective usage that has nothing to do with proof:
 *
 *   BLOCKED  "Review your schedule before you post."   ← imperative verb, not a noun
 *   BLOCKED  "review-ready posts"                       ← compound adjective
 *
 * The plural `reviews`/`testimonials`/`ratings` stay unconditional — ad copy
 * essentially never uses the plural as a verb ("Read the reviews before you
 * buy" is unambiguously the noun) — but the SINGULAR `review`/`rating`/
 * `testimonial` and the bare word `rated` now only count as proof when a
 * number or star rating actually sits next to them: "a 9.4 rating", "one
 * 5-star review", "rated #1". A singular noun or a verb/adjective with no
 * number nearby is not evidence of anything, fabricated or otherwise.
 *
 * That was not a cosmetic over-reach either time. A violation here fails the
 * whole generation after burning a repair round-trip, so an honest guard was
 * turning valid copy into a dead end — observed live on the ad-library
 * journey, where "write my own version" spent 150s and returned no ad at all.
 *
 * WHAT STAYS BLOCKED, deliberately:
 *   - attribution phrases: trusted by, loved by, already saying
 *   - a proof verb attached to a group: customers/fans/players/users say|love|rave|agree
 *   - a quantified group: thousands of customers, 2,000 players
 *   - star ratings in any form, and "rated #N"
 *   - the PLURAL nouns `reviews`, `testimonials`, `ratings`, because asserting
 *     that reviews or ratings EXIST is the fabrication when none are on file
 *   - a SINGULAR `review`/`rating`/`testimonial` when a number sits near it
 */
export const SOCIAL_PROOF_PATTERN = new RegExp(
  [
    // Attribution phrases that only ever introduce third-party endorsement.
    'trusted by|loved by|already saying|rave reviews',
    // Asserting the artefacts of proof exist at all — plural only. The
    // singular `review` is excluded here: it is also the imperative verb
    // ("Review your schedule") and a compound modifier ("review-ready
    // posts"), so singular only counts as proof below, gated on a nearby
    // number. `rating`/`testimonial` keep their singular too — no false
    // positive was observed for those, and this fix is scoped to what was.
    '\\b(?:reviews|testimonials?|ratings?)\\b',
    // Star ratings ("4.8 stars", "five-star", "5 star rating") and numeric
    // "rated #N" claims. Bare `rated` with no number attached is NOT here —
    // "well-rated" and "highly rated" are ordinary adjectives, not a claim.
    '\\b(?:\\d+(?:\\.\\d+)?|five|four)[\\s-]*stars?\\b|\\bstar[\\s-]rated\\b|\\brated\\s*#\\d+\\b',
    /**
     * A singular `review`/`rating`/`testimonial` only counts as proof when a
     * number sits within two words of it, either direction — "a 9.4 rating",
     * "one 5-star review", "2,000-review average". No number nearby means
     * this is ordinary prose, not a claim.
     */
    '\\b\\d[\\d,]*(?:\\.\\d+)?\\s+(?:\\w+\\s+){0,2}(?:review|rating|testimonial)\\b|' +
      '\\b(?:review|rating|testimonial)\\b\\s+(?:\\w+\\s+){0,2}\\d[\\d,]*(?:\\.\\d+)?\\b',
    /**
     * A group of people credited with an opinion.
     *
     * Up to three words may sit between the group and the verb — "fans ALREADY
     * say", "customers EVERYWHERE love", "players ACROSS THE COUNTRY report".
     * An earlier cut allowed only "are"/"have" and let "Fans already say this
     * widget is perfect" through, which an existing test caught. Punctuation
     * bounds the run, so it cannot leap a sentence break.
     */
    '\\b(?:customers?|fans?|players?|users?|shoppers?|reviewers?|golfers?|athletes?|students?|patients?|clients?|members?|alumni|graduates?)\\b' +
      '(?:\\s+\\w+){0,3}\\s+(?:say|says|said|saying|love|loves|loved|rave|raving|agree|agrees|report|reports|call it|swear by)\\b',
    // A quantified group, which is a proof claim even without a verb.
    '\\b(?:thousands|hundreds|millions|dozens|\\d[\\d,]*)\\s+of\\s+' +
      '(?:customers?|fans?|players?|users?|shoppers?|reviewers?|students?|patients?|clients?|members?|alumni|graduates?)\\b',
  ].join('|'),
  'i',
);
export const UNSUPPORTED_CERTAINTY_PATTERN =
  /\b(?:always|never|guarantee(?:d)?|exactly|perfect(?:ly)?|effortless(?:ly)?)\b/i;
/**
 * FABRICATED SCARCITY. Urgency is legitimate only when it names a constraint
 * that is real and will actually be enforced — a limited run, a dated window, a
 * price rise that genuinely happens. None of that is knowable from a copy
 * generation request, so a model inventing "only 3 left" or "24 hours only" is
 * manufacturing a fact about inventory or pricing it has no access to. This is
 * the ad-copy instance of the repo's standing no-hallucination law, and it is
 * enforced here rather than asked for in the prompt because a prompt
 * instruction is a request and a regex is a gate.
 */
export const FABRICATED_SCARCITY_PATTERN =
  /\b(?:only \d+ left|\d+ (?:spots?|seats?|units?) (?:left|remaining)|last chance|ends (?:tonight|today|in \d+)|\d+ hours? only|while supplies last|selling fast|almost gone|\d+ people (?:are )?(?:viewing|watching)|limited time only)\b/i;

/**
 * SLOP VOCABULARY. Words that assert importance instead of demonstrating it.
 * Named in the incumbent's own offer doctrine as the tell of a weak offer being
 * dressed up ("specificity beats superlatives").
 *
 * This is the HARD-FAIL "CORE" tier of `src/lib/content-doctrine/slop.ts`'s
 * two-tier vocabulary (`SLOP_PHRASES_CORE` — see that file's header comment
 * for the full provenance and why CORE is kept deliberately narrower than the
 * `STANDARD` tier used for prompt bans and post-hoc warnings). The literal
 * regex below is kept EXACTLY as written — not computed from
 * `SLOP_PHRASES_CORE` at runtime — for one reason: `paid-copy-slop-gate.ts`
 * re-declares this exact regex verbatim, and
 * `paid-copy-slop-gate.test.ts` asserts the two literals stay byte-identical
 * by reading both files' source at test time. Replacing this literal with a
 * computed pattern would break that parity test, and that file is out of
 * scope for the slop-vocabulary consolidation this comment describes.
 * `ad-contract.test.ts` instead proves this pattern and `SLOP_PHRASES_CORE`
 * agree on a shared corpus, so the two vocabularies cannot silently drift
 * even though one is not syntactically generated from the other.
 */
export const SLOP_VOCABULARY_PATTERN =
  /\b(?:game[- ]?chang(?:er|ing)|revolutionary|cutting[- ]edge|state[- ]of[- ]the[- ]art|world[- ]class|best[- ]in[- ]class|next[- ]level|supercharge|unlock the (?:power|secret)|elevate your|unleash)\b/i;

export const UNSUPPORTED_PERFORMANCE_OUTCOME_PATTERN =
  /\b(?:every time|win(?:ning|s)?|best game|hit your mark|direct your shots|out of bounds|place the ball|wild swings|accuracy|stability|unforced errors?|explosive|sweet spot|long rallies|solves?\s+(?:this|that|your|the))\b/i;

/**
 * Health: a claim that the product cures, treats or is clinically proven to affect the body.
 * A regulated statement the seller cannot substantiate through an ad line.
 */
export const HEALTH_CLAIM_PATTERN =
  /\b(?:cures?|heals?|clinically (?:proven|tested|shown)|medically (?:proven|tested)|doctor[- ]recommended|dermatologist[- ](?:tested|recommended|approved)|fda[- ](?:approved|cleared)|anti[- ]?(?:aging|inflammatory)|detox(?:es|ifies|ify)?|boosts? (?:your )?immun(?:e|ity)|relieves? (?:pain|stress|anxiety)|lose weight|weight loss|pain[- ]free|before[- ]and[- ]after|get(?:ting)? rid of|diagnos(?:e|es|ed|ing|is)|treat(?:s|ed|ing)? (?:your |the |chronic )?(?:pain|back|knee|neck|shoulder|anxiety|depression|insomnia|acne|condition|injur\w+|arthritis|diabetes|migraines?|disease|disorder)|treatments? (?:for|of)|reverse[sd]? (?:your |the )?(?:aging|diabetes|damage|disease|condition|hair loss|arthritis|decline)|fix(?:es)? your (?:back|knee|neck|shoulder|posture|pain|condition|skin|acne|sleep|anxiety|injury)|results? in (?:one|a single|1) (?:visit|session|treatment))\b/i;

/**
 * Earnings and outcome: an income, job or time-bound result the seller states for the people who
 * use the product. An unsubstantiated earnings or results claim is a regulated statement the seller
 * cannot back through an ad line, and it is the claim a course or a clinic reaches for first.
 * "Earn" counts only beside a dollar amount, so a price ("$49 a month") is not matched.
 */
export const EARNINGS_CLAIM_PATTERN = new RegExp(
  [
    '\\b(?:earn(?:s|ed|ing|ings)?|income|profits?|bring(?:s|ing)? in|pull(?:s|ed|ing)? in|generate[sd]?)\\b(?:\\s+\\S+){0,6}?\\s+\\$\\s?\\d',
    '\\b(?:six|seven|\\d+)[- ]figures?\\b',
    '\\b(?:make|making|made) (?:real |extra |easy |serious )?money\\b|\\bpassive income\\b|\\bfinancial freedom\\b|\\breplace your (?:full[- ]time |current )?income\\b|\\bquit your (?:9[- ]to[- ]5|job)\\b',
    '\\b(?:land(?:ed|ing)?|secure[sd]?) (?:a|an|your|that|the|their) (?:dream |new |first |six[- ]figure )?(?:job|role|promotion|raise)\\b|\\b(?:get|got|getting) hired\\b',
    '\\b(?:pass|success|completion|placement|hire|employment) rates?\\b',
    '\\bresults? (?:in|within|after) (?:just )?(?:\\d+|a|an|one|two|three|four|five|six|seven|eight|nine|ten) (?:minutes?|hours?|days?|weeks?|months?|years?|visits?|sessions?)\\b',
  ].join('|'),
  'i',
);

/** Certification: a named or generic certification, licence or standard the seller states about the product. */
export const CERTIFICATION_CLAIM_PATTERN =
  /\b(?:certified|certification|accredited|licensed|usda|fda|organic|oeko[- ]?tex|gots|fair[- ]?trade|b[- ]?corp|iso[- ]?\d+|ce[- ]marked|cpsia|astm|tested and approved)\b/i;

/** Origin: a United States origin claim, which is regulated ("Made in USA"). Other origins are not matched. */
export const ORIGIN_CLAIM_PATTERN =
  /\b(?:(?:made|built|assembled|manufactured|crafted|produced)\s+(?:and\s+\w+\s+)?in\s+(?:the\s+)?(?:usa|u\.s\.a?\.?|united states|america)|american[- ]made|proudly american|100%\s+(?:american|usa))\b/i;

/** Award: a prize, ranking or "best seller" status that someone else conferred. */
export const AWARD_CLAIM_PATTERN =
  /\b(?:award[- ]winning|awarded|best[- ]?sell(?:er|ing)|number (?:one|1)|#\s?1|top[- ]rated|top[- ]ranked|voted|winner of|ranked)\b/i;

/** Comparative: better, best or first against an unnamed field. */
export const COMPARATIVE_CLAIM_PATTERN =
  /\b(?:better than|best|superior|outperforms?|beats|than (?:the )?(?:competition|competitors|others|any other|leading)|most (?:popular|trusted|durable|comfortable|advanced)|unlike (?:other|most)|the only)\b/i;

export type AdClaimClass =
  | 'health'
  | 'earnings'
  | 'certification'
  | 'origin'
  | 'award'
  | 'comparative'
  | 'certainty'
  | 'performance'
  | 'scarcity'
  | 'social_proof'
  | 'slop';

export const AD_CLAIM_CLASS_PATTERNS: Readonly<Record<AdClaimClass, RegExp>> = {
  health: HEALTH_CLAIM_PATTERN,
  earnings: EARNINGS_CLAIM_PATTERN,
  certification: CERTIFICATION_CLAIM_PATTERN,
  origin: ORIGIN_CLAIM_PATTERN,
  award: AWARD_CLAIM_PATTERN,
  comparative: COMPARATIVE_CLAIM_PATTERN,
  certainty: UNSUPPORTED_CERTAINTY_PATTERN,
  performance: UNSUPPORTED_PERFORMANCE_OUTCOME_PATTERN,
  scarcity: FABRICATED_SCARCITY_PATTERN,
  social_proof: SOCIAL_PROOF_PATTERN,
  slop: SLOP_VOCABULARY_PATTERN,
};

/** The classes `checkAdContract` runs on a finished ad: the seller-fact classes that regulate what a person is told they will earn or how their body will change, plus the original five. */
export const AD_CONTRACT_CLAIM_CLASSES = [
  'health',
  'earnings',
  'social_proof',
  'certainty',
  'performance',
  'scarcity',
  'slop',
] as const satisfies readonly AdClaimClass[];

/** Every class a seller fact is checked against. */
export const SELLER_FACT_CLAIM_CLASSES = [
  'health',
  'earnings',
  'certification',
  'origin',
  'award',
  'comparative',
  'certainty',
  'performance',
  'scarcity',
  'social_proof',
  'slop',
] as const satisfies readonly AdClaimClass[];

/** The classes in `classes` that `text` matches, in the order given. Empty means no claim matched. */
export function findAdClaimClasses(
  text: string,
  classes: readonly AdClaimClass[] = SELLER_FACT_CLAIM_CLASSES,
): AdClaimClass[] {
  return classes.filter((claimClass) => AD_CLAIM_CLASS_PATTERNS[claimClass].test(text));
}
